import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { SupportCategory, SupportStatus, SupportTicket } from '@reims-local/types';
import { supportApi } from '../lib/api';

const CATEGORY_LABEL: Record<SupportCategory, string> = {
  bug: '🐛 Bug',
  suggestion: '💡 Suggestion',
  question: '❓ Question',
  critical: '🚨 Critique',
};

export function Support() {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supportApi.list()
      .then((res) => setTickets(res.data))
      .catch((err) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Support ({tickets.length})</h2>
        <div className="spacer" />
        <Link to="/support/new"><button className="primary">+ Nouveau ticket</button></Link>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {loading ? (
        <p>Chargement…</p>
      ) : tickets.length === 0 ? (
        <div className="card">
          <p>Aucun ticket. Ouvrez-en un pour contacter Snitch Team (bugs, questions, suggestions).</p>
        </div>
      ) : (
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Sujet</th>
              <th>Catégorie</th>
              <th>Statut</th>
              <th>Ouvert le</th>
              <th>Maj</th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr key={t.id}>
                <td><Link to={`/support/${t.id}`}>{t.ticket_number}</Link></td>
                <td>{t.subject}</td>
                <td>{CATEGORY_LABEL[t.category as SupportCategory]}</td>
                <td><span className={`badge ${statusClass(t.status as SupportStatus)}`}>{statusLabel(t.status as SupportStatus)}</span></td>
                <td className="muted">{fmt(t.created_at)}</td>
                <td className="muted">{fmt(t.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

export function SupportCreate() {
  const [category, setCategory] = useState<SupportCategory>('question');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await supportApi.create({ category, subject, message });
      window.location.assign(`/support/${res.data.id}`);
    } catch (err) {
      const code = (err as Error).message;
      setError(code === 'rate_limited' ? 'Trop de tickets dans l’heure (limite : 10/h).' : (err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Link to="/support">← Retour</Link>
      <h2 style={{ marginTop: 12 }}>Nouveau ticket</h2>
      {error && <div className="alert alert-error">{error}</div>}

      <form className="card" onSubmit={submit}>
        <div className="form-row">
          <label>Catégorie</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {(['bug', 'question', 'suggestion', 'critical'] as SupportCategory[]).map((c) => (
              <button
                key={c}
                type="button"
                className={category === c ? 'primary' : ''}
                onClick={() => setCategory(c)}
                style={{ padding: '6px 12px' }}
              >
                {CATEGORY_LABEL[c]}
              </button>
            ))}
          </div>
          <div className="muted" style={{ marginTop: 4 }}>
            Délais : Critique 4h/24h · Bug & Question 24h/48h · Suggestion 24h sans engagement résolution.
          </div>
        </div>
        <div className="form-row">
          <label>Sujet (80 caractères max)</label>
          <input required maxLength={80} value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div className="form-row">
          <label>Message</label>
          <textarea required rows={6} value={message} onChange={(e) => setMessage(e.target.value)} />
        </div>
        <button className="primary" type="submit" disabled={submitting}>
          {submitting ? 'Envoi…' : 'Envoyer le ticket'}
        </button>
      </form>
    </>
  );
}

export function SupportDetail({ ticketId }: { ticketId: string }) {
  const [detail, setDetail] = useState<Awaited<ReturnType<typeof supportApi.get>>['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  async function load() {
    try {
      const res = await supportApi.get(ticketId);
      setDetail(res.data);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    const intv = setInterval(() => void load(), 30_000);
    return () => clearInterval(intv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  async function submitReply(e: React.FormEvent) {
    e.preventDefault();
    if (!reply.trim()) return;
    setSending(true);
    try {
      await supportApi.reply(ticketId, reply);
      setReply('');
      await load();
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setSending(false);
    }
  }

  async function markResolved() {
    if (!confirm('Marquer ce ticket comme résolu ?')) return;
    try {
      await supportApi.resolve(ticketId);
      await load();
    } catch (err) {
      alert((err as Error).message);
    }
  }

  if (loading) return <p>Chargement…</p>;
  if (error) return <div className="alert alert-error">{error}</div>;
  if (!detail) return null;
  const { ticket, messages } = detail;
  const status = ticket.status as SupportStatus;

  return (
    <>
      <Link to="/support">← Retour aux tickets</Link>
      <h2 style={{ marginTop: 12 }}>
        {ticket.ticket_number} — {ticket.subject}
      </h2>
      <div style={{ marginBottom: 16 }}>
        <span className="badge">{CATEGORY_LABEL[ticket.category as SupportCategory]}</span>{' '}
        <span className={`badge ${statusClass(status)}`}>{statusLabel(status)}</span>
      </div>

      <div className="card">
        {messages.map((m) => (
          <div key={m.id} style={{ marginBottom: 16, padding: 12, background: m.sender === 'merchant' ? '#fff0f3' : '#f1f5f9', borderRadius: 8 }}>
            <div className="muted" style={{ fontSize: 12 }}>
              {m.sender === 'merchant' ? 'Vous' : 'Snitch Team'} — {fmt(m.created_at)}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', marginTop: 4 }}>{m.content}</div>
          </div>
        ))}
      </div>

      {status !== 'closed' && status !== 'resolved' && (
        <form className="card" onSubmit={submitReply} style={{ marginTop: 16 }}>
          <h3 style={{ marginTop: 0 }}>Répondre</h3>
          <div className="form-row">
            <textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Votre message…" />
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button type="submit" className="primary" disabled={sending}>
              {sending ? 'Envoi…' : 'Envoyer'}
            </button>
            <button type="button" onClick={markResolved}>Marquer comme résolu</button>
          </div>
        </form>
      )}

      {status === 'resolved' && (
        <div className="alert alert-success" style={{ marginTop: 16 }}>
          Ce ticket est résolu. Répondez pour le rouvrir.
          <form onSubmit={submitReply} style={{ marginTop: 12 }}>
            <textarea rows={3} value={reply} onChange={(e) => setReply(e.target.value)} />
            <button type="submit" disabled={sending} style={{ marginTop: 8 }}>Rouvrir avec un nouveau message</button>
          </form>
        </div>
      )}
    </>
  );
}

function statusClass(s: SupportStatus): string {
  if (s === 'resolved') return 'green';
  if (s === 'closed') return '';
  if (s === 'waiting') return 'blue';
  return 'orange';
}
function statusLabel(s: SupportStatus): string {
  const m: Record<SupportStatus, string> = {
    new: 'Nouveau', in_progress: 'En cours', waiting: 'En attente', resolved: 'Résolu', closed: 'Fermé',
  };
  return m[s];
}
function fmt(iso: string): string {
  try {
    return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso));
  } catch { return iso; }
}
